/**
 * テンプレート選択ロジック (純粋関数)。
 *
 * SPEC §4.2: 同一カテゴリ内で直前と同じパターンを避けて選ぶ。
 * シード値で決定論的に選択できるようにしてテスト可能性を確保。
 */

import type { CategoryId } from '@shared/types';

import { TEMPLATES } from './templates';

export interface SelectionState {
  readonly lastUsed: ReadonlyMap<CategoryId, number>;
}

export function emptyState(): SelectionState {
  return { lastUsed: new Map<CategoryId, number>() };
}

export interface SelectedTemplate {
  readonly categoryId: CategoryId;
  readonly template: string;
  readonly patternIdx: number;
  readonly nextState: SelectionState;
}

/**
 * カテゴリ直前のパターンを避けて 1 つ選ぶ。
 *
 * @param categoryId - 対象カテゴリ
 * @param state - 過去の選択履歴
 * @param random - [0, 1) を返す乱数生成器 (テスト時に差替え)
 */
export function selectTemplate(
  categoryId: CategoryId,
  state: SelectionState,
  random: () => number = Math.random,
): SelectedTemplate {
  const patterns = TEMPLATES[categoryId];
  const last = state.lastUsed.get(categoryId);
  const candidates: number[] = [];
  for (let i = 0; i < patterns.length; i++) {
    if (i !== last) candidates.push(i);
  }
  if (candidates.length === 0) {
    // フォールバック: パターンが 1 個しかない場合
    candidates.push(0);
  }
  const pickedPosition = Math.floor(random() * candidates.length);
  const patternIdx = candidates[pickedPosition] ?? 0;
  const template = patterns[patternIdx] ?? patterns[0];
  const nextMap = new Map(state.lastUsed);
  nextMap.set(categoryId, patternIdx);
  return {
    categoryId,
    template,
    patternIdx,
    nextState: { lastUsed: nextMap },
  };
}
